import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Empty, Modal, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CollapseBlock,
  Spinner,
} from '../../../../../components/common/index.jsx';

const StudentApplicationsModal = ({
  applications,
  isLoading = false,
  isOpen = false,
  onClose,
  studentName,
}) => {
  const { t } = useTranslation();

  const groupedApplications = useMemo(() => {
    if (!Array.isArray(applications) || applications.length === 0) {
      return [];
    }

    const groups = {};

    applications.forEach((application) => {
      const programId = application.study_program_id ?? 'unknown';
      const programName =
        application.studyProgram?.name ??
        application.study_program?.name ??
        t('Neznámy študijný program');

      if (!groups[programId]) {
        groups[programId] = {
          programId,
          programName,
          applications: [],
        };
      }

      groups[programId].applications.push(application);
    });

    Object.values(groups).forEach((group) => {
      group.applications.sort((left, right) => {
        if ((left.priority ?? 0) !== (right.priority ?? 0)) {
          return (left.priority ?? 0) - (right.priority ?? 0);
        }

        return (right.year ?? 0) - (left.year ?? 0);
      });
    });

    return Object.values(groups).sort((left, right) =>
      left.programName.localeCompare(right.programName),
    );
  }, [applications, t]);

  const columns = useMemo(
    () => [
      {
        title: t('Priorita'),
        dataIndex: 'priority',
        key: 'priority',
        width: 100,
        sorter: (left, right) => (left.priority ?? 0) - (right.priority ?? 0),
        defaultSortOrder: 'ascend',
      },
      {
        title: t('Názov projektu'),
        dataIndex: ['project', 'name'],
        key: 'project_name',
        render: (value) => value || '–',
      },
      {
        title: t('Rok'),
        dataIndex: 'year',
        key: 'year',
        width: 100,
        render: (value) => value || '–',
      },
      {
        title: t('Priorita učiteľa'),
        dataIndex: 'teacher_priority',
        key: 'teacher_priority',
        width: 140,
        render: (value) => value || '–',
      },
    ],
    [t],
  );

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={1000}
      title={
        studentName
          ? t('Prihláška študenta {{name}} na inž.projekty', { name: studentName })
          : t('Prihláška študenta na inž. projekty')
      }
      destroyOnHidden
    >
      {isLoading ? (
        <Spinner />
      ) : groupedApplications.length > 0 ? (
        groupedApplications.map((group) => (
          <CollapseBlock
            key={group.programId}
            title={`${group.programName} (${group.applications.length})`}
          >
            <Table
              rowKey="id"
              columns={columns}
              dataSource={group.applications}
              pagination={false}
              size="small"
            />
          </CollapseBlock>
        ))
      ) : (
        <Empty description={t('Študent nemá žiadne prihlášky na projekty')} />
      )}
    </Modal>
  );
};

StudentApplicationsModal.propTypes = {
  applications: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  studentName: PropTypes.string,
};

export default StudentApplicationsModal;
